<script setup lang="ts">
import { computed, onMounted, reactive, ref, h, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { capitalize } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import max from 'lodash/max';
import { DateTime } from 'luxon';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { AIR_ONLY, STATUS_OPEN, LANG_FRENCH, ROUNDTRIP, OPENJAW, ONEWAY, AIR } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useSubmitQuoteStore } from '@/store/SubmitQuoteStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useAirPortionStore } from '@/store/AirPortionStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useCityStore } from '@/store/CityStore';
import { useTabComposable } from './useTabComposable';
import { useModalComposable } from '@/views/composable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import AirAdditionalForm from '@/components/tabs/AirQuoteForm/AirAdditionalForm.vue';
import AirCalendarDurationForm from '@/components/tabs/AirQuoteForm/AirCalendarDurationForm.vue';
import AirClassInputNumber from '@/components/tabs/AirQuoteForm/AirClassInputNumber.vue';
import AirGatewayDestinationSelect from '@/components/tabs/AirQuoteForm/AirGatewayDestinationSelect.vue';
import AirOpenJawReturningForm from '@/components/tabs/AirQuoteForm/AirOpenJawReturningForm.vue';
import FlightInformationModal from '@/components/modal/FlightInformationModal.vue';
import SubmitNotificationModal from '@/components/modal/SubmitNotificationModal.vue';
import TripType from './TripType.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';

interface Props {
  instanceForm?: object;
  advancedForm: object;
  isValidation: boolean;
  isFlight?: boolean;
  avgPaxRoomData: number;
  selectedLanguage: string;
  quoteIntro: object;
  calendarData?: object;
}
const props = defineProps<Props>();

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const currentLanguage = getCurrentLocale();
const { getUserParam, getUserQuote, formatAirport, handleRedirectFlight, validateDateTime, renderOriginQuote, getQuoteDepartureData, getLimitDate } = useTabComposable();
const { handleOpenErrorMessage } = useModalComposable();
const adminQuoteStore = useAdminQuoteStore();
const adminSetupStore = useAdminSetupStore();
const submitQuoteStore = useSubmitQuoteStore();
const viewQuoteStore = useViewQuoteStore();
const airPortionStore = useAirPortionStore();
const cityStore = useCityStore();
const diffDays: any = ref(0);
const isRoundtrip = ref(false);
const isOpenJaw = ref(false);
const isOneWay = ref(false);

const emits = defineEmits<{
  submit: [];
  clear: [];
}>();

const airs = reactive([
  {
    gateway: '',
    destination: '',
    dateRange: [],
    depDate: null,
    duration: '0',
    adults: 0,
    children: 0,
    service: [{ type: 'Economy' }, { type: 'Business' }],
    directFlight: false,
    additionalNote: '',
    businessAdults: 0,
    businessChildren: 0,
    isEconomy: false,
    isBusiness: false,
    returningFrom: '',
    returningTo: '',
    tripTypeSelected: ROUNDTRIP
  }
]);

const rules = {
  $each: helpers.forEach({
    gateway: {
      required: helpers.withMessage(t('validate.required'), required)
    },
    destination: {
      required: helpers.withMessage(t('validate.required'), required)
    },
    dateRange:
      isRoundtrip.value && isOpenJaw.value
        ? {
            required: helpers.withMessage(t('validate.required'), required)
          }
        : null,
    depDate: isOneWay.value
      ? {
          required: helpers.withMessage(t('validate.required'), required)
        }
      : null,
    adults: {
      required: helpers.withMessage(t('validate.required'), required)
    },
    businessAdults: {
      required: helpers.withMessage(t('validate.required'), required)
    },
    returningFrom: isOpenJaw.value
      ? {
          required: helpers.withMessage(t('validate.required'), required)
        }
      : null,
    returningTo: isOpenJaw.value
      ? {
          required: helpers.withMessage(t('validate.required'), required)
        }
      : null
  })
};

const v$ = useVuelidate(rules, airs);
const flightTypesOption = [{ type: 'Outbound' }, { type: 'Inbound' }];
const isAvancedForm = ref(true);
const isShowError = ref(false);
const isClearAgencyInfo = ref(false);
const minDateRangeValue = ref(new Date(DateTime.utc().plus({ days: 60 })));
const maxDateRangeValue = ref<Date | undefined>(new Date(DateTime.utc().plus({ days: 360 })));
const limitCalendar = ref({});
const filterGate = ref();
const filterDes = ref();
const filterReturningFrom = ref();
const filterReturningTo = ref();
const isReturnDate = ref([false]);
const listAirline = ref([]);
const listDestAir = ref([]);
const optionConfigValue = ref(1);
const disableDate = ref(false);

const listReturning = computed(() => cityStore.returning);
const isLoading = computed(() => submitQuoteStore.selectIsLoading);
const quote = computed(() => viewQuoteStore.selectQuote);
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);
const airOptionConfig = computed(() => adminSetupStore.optionConfig?.airOptionConfig);
const airMinimumConfig = computed(() => adminSetupStore.minimumConfig?.airConfig);

const gatewayAirports = computed(() => {
  return formatAirport(
    listReturning.value.filter((returning: any) => returning.gatewayStatus === STATUS_OPEN && returning.gatewayApplicability.isAir),
    currentLanguage
  );
});
const destinationAirports = computed(() => {
  return formatAirport(
    listReturning.value.filter((returning: any) => returning.destinationStatus === STATUS_OPEN && returning.destinationApplicability.isAir),
    currentLanguage
  );
});

const airportsReturningFrom = computed(() => {
  return formatAirport(
    listReturning.value.filter((returning: any) => returning.destinationStatus === STATUS_OPEN && returning.destinationApplicability.isAir),
    currentLanguage
  );
});

const airportsReturningTo = computed(() => {
  return formatAirport(
    listReturning.value.filter((returning: any) => returning.gatewayStatus === STATUS_OPEN && returning.gatewayApplicability.isAir),
    currentLanguage
  );
});

const getListAirline = async () => {
  const response = await adminQuoteStore.getAirlines();
  listAirline.value = response;
};

const getDestAir = async () => {
  const response = await cityStore.getDestAir();
  listDestAir.value = response;
};
getDestAir();

const checkPassengerAirs = (airList: any) => {
  return airList?.map((airItem: any) => airItem.adults + airItem.businessAdults + airItem.children + airItem.businessChildren).some((total: number) => total < airMinimumConfig.value.minPax);
};

const updatePayload = (isAdvanced: boolean, propsValue: any) => {
  if (isAdvanced) {
    return {
      advanced: true,
      gName: propsValue.advancedForm[0].groupName,
      gContact: propsValue.advancedForm[0].groupContact,
      gType: propsValue.advancedForm[0].groupType,
      gTypeFr: propsValue.advancedForm[0].groupTypeFr,
      gTypeId: propsValue.advancedForm[0].groupTypeId
    };
  } else {
    return {
      advanced: false,
      gName: propsValue.instanceForm[0].groupName,
      gContact: propsValue.instanceForm[0].groupContact,
      gType: propsValue.instanceForm[0].groupType,
      gTypeFr: propsValue.instanceForm[0].groupTypeFr,
      gTypeId: propsValue.instanceForm[0].groupTypeId
    };
  }
};

const showModalErrorAir = () => {
  const modalRef = dialog.open(ValidateQuoteFormNotification, {
    props: {
      modal: true,
      header: t('common.labels.unable_to_process')
    },
    data: {
      message: t('notification.over_days')
    },
    templates: {
      footer: () => {
        return [h('div', { class: 'text-center' }, h(Button, { autofocus: true, label: t('common.buttons.close'), onClick: () => modalRef.close() }))];
      }
    }
  });
};

const openModalTimeOut = () => {
  clearAllDataAirs();
  dialog.open(SubmitNotificationModal, {
    props: {
      header: t('advance_quote.confirm_quote_id'),
      modal: true
    }
  });
};

const validateAdvanced = () => {
  let isValidateAdvanced = true;
  airs.forEach((air: any) => {
    if (air.tripTypeSelected === ROUNDTRIP && (!air.dateRange[0] || !air.dateRange[1])) {
      isValidateAdvanced = false;
    }
    if (air.tripTypeSelected === OPENJAW && (!air.dateRange[0] || !air.dateRange[1] || !air.returningFrom || !air.returningTo)) {
      isValidateAdvanced = false;
    }
    if (air.tripTypeSelected === ONEWAY && !air.depDate) {
      isValidateAdvanced = false;
    }
  });
  return isValidateAdvanced;
};

const submitPayloadToCheckBrochure = (payload: any) => {
  cityStore.checkJawBrochure(AIR, payload).then((res: any) => {
    if (!res) {
      showUnProcessBrochure();
    } else {
      handleSubmit();
    }
  });
};

const handleCheckBrochure = async () => {
  const payload: any = [];
  const dataNotRoundTrip = airs.filter((air: any) => air.tripTypeSelected !== ROUNDTRIP);
  emits('submit');
  isShowError.value = true;
  await v$.value.$validate();
  if (!v$.value.$error && validateAdvanced()) {
    airs.forEach((air) => {
      const gateway = {
        pk: air.gateway?.pk,
        sk: air.gateway?.sk
      };

      const destination = {
        pk: air.destination?.pk,
        sk: air.destination?.sk
      };

      const returningFrom = {
        pk: air.returningFrom?.pk,
        sk: air.returningFrom?.sk
      };

      const returningTo = {
        pk: air.returningTo?.pk,
        sk: air.returningTo?.sk
      };

      if (air.tripTypeSelected === ONEWAY) {
        payload.push(gateway, destination);
      }
      if (air.tripTypeSelected === OPENJAW) {
        payload.push(gateway, destination, returningFrom, returningTo);
      }
    });

    if (dataNotRoundTrip.length) {
      submitPayloadToCheckBrochure(payload);
    } else {
      handleSubmit();
    }
  } else {
    handleOpenErrorMessage(t('notification.fill_required'));
  }
};

const showUnProcessBrochure = () => {
  const dialogRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      title: t('common.labels.unable_to_process'),
      message: t('common.labels.process_jaw'),
      isAlignCenter: true
    },
    props: {
      modal: true
    },
    templates: {
      header: () => {
        return [h('div')];
      },
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('common.buttons.ok'),
              autofocus: true,
              onClick: () => dialogRef.close()
            })
          )
        ];
      }
    }
  });
};

const handleSearchFlight = async (payload: any, payloadOpenJaw: any, payloadOneway: any) => {
  submitQuoteStore.isLoading = true;
  for (let airValue = 0; airValue < airs.length; airValue++) {
    if (airs[airValue].tripTypeSelected === ROUNDTRIP) {
      await airPortionStore.getFlightInformation([payload[airValue]]);
    }
    if (airs[airValue].tripTypeSelected === OPENJAW) {
      await airPortionStore.searchFlightOptionJaw([payloadOpenJaw[airValue]]);
    }
    if (airs[airValue].tripTypeSelected === ONEWAY) {
      await airPortionStore.searchFlightOptionOneWay([payloadOneway[airValue]]);
    }
  }
  submitQuoteStore.isLoading = false;
};

const handleUpdatePayloadAir = async (payload: any, listFlight: any) => {
  const newPayloadAir = updatePayloadAir(payload.airs, listFlight, listAirline.value);
  payload.airs = newPayloadAir;
  submitQuoteStore
    .handleSubmit(payload, undefined, openModalTimeOut)
    .then(() => {
      removeAirs();
      dialog.open(SubmitNotificationModal, {
        props: {
          header: t('my_quote.quote_id'),
          modal: true
        }
      });
    })
    .catch((e) => {
      toast.add({ severity: 'error', summary: '', detail: e.message || t('notification.save_error'), life: 5000 });
    });
};

const openFlightInformationModal = (airsTemp: any) => {
  let payload = createPayloadObject(airsTemp);

  const dialogRef = dialog.open(FlightInformationModal, {
    data: {
      flightInformation: airPortionStore?.selectFlightInformation
    },
    props: {
      header: t('create_quote.flight_information'),
      modal: true
    },
    emits: {
      isSave: (inbound: any, outbound: any, listFlight: any) => {
        dialogRef.close();
        handleUpdatePayloadAir(payload, listFlight);
      },
      onClose: () => {
        airPortionStore.clearListFlightInformation();
      }
    },
    onClose: () => {
      airPortionStore.clearListFlightInformation();
    }
  });
  payload = { ...payload, ...updatePayload(isAvancedForm.value, props) };
};

const handleSubmit = async () => {
  if (props.isValidation) {
    if (checkPassengerAirs(airs) && !isWorksheetRequote.value) {
      const dialogRef = dialog.open(ValidateQuoteFormNotification, {
        data: {
          message: t('advance_quote.form.min_pax_air', { minPax: airMinimumConfig.value.minPax })
        },
        props: {
          header: t('advance_quote.form.notice'),
          modal: true
        },
        templates: {
          footer: () => {
            return [
              h(
                'div',
                { class: 'text-center' },
                h(Button, {
                  label: t('advance_quote.form.return_to_form'),
                  autofocus: true,
                  onClick: () => dialogRef.close()
                })
              )
            ];
          }
        }
      });
      return;
    }

    const arrPayload: any = [];
    const airsTemp: any = [];
    const payloadOpenJaw: any = [];
    const payloadOneway: any = [];
    airs.forEach((air, index) => {
      const formAir = createFormAirObject(air);
      airsTemp.push(formAir);
      const flightPayload = createFlightPayloadObject(air);

      if (air.tripTypeSelected === ROUNDTRIP) {
        arrPayload[index] = flightPayload;
      }
      if (air.tripTypeSelected === OPENJAW) {
        payloadOpenJaw[index] = [
          {
            gateCode: air.gateway.airportCode,
            destCode: air.destination.airportCode,
            depDate: formatDate(air.dateRange[0]),
            direct: air.directFlight
          },
          {
            gateCode: air.returningFrom.airportCode,
            destCode: air.returningTo.airportCode,
            depDate: formatDate(air.dateRange[1]),
            direct: air.directFlight
          }
        ];
      }
      if (air.tripTypeSelected === ONEWAY) {
        payloadOneway[index] = {
          gateCode: air.gateway.airportCode,
          destCode: air.destination.airportCode,
          depDate: formatDate(air.depDate),
          direct: air.directFlight
        };
      }
    });

    const checkOverDateFlight = airsTemp.some((item: any) => validateDateTime(item?.depDate, props.advancedForm[0].groupType));
    if (checkOverDateFlight) {
      showModalErrorAir();
    } else {
      await handleSearchFlight(arrPayload, payloadOpenJaw, payloadOneway);

      if (!submitQuoteStore.isLoading) {
        openFlightInformationModal(airsTemp);
      }
      isShowError.value = false;
    }
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

function createFormAirObject(air: any, quoteId?: any) {
  return {
    sk: quoteId,
    gate: air.gateway?.airportNameEn,
    gateFr: air.gateway?.airportNameFr,
    itinerary: air.tripTypeSelected,
    duration: air.duration,
    gateCode: air.gateway.airportCode,
    dest: `${air.destination?.airportNameEn} (${air.destination?.countryNameEn})`,
    destFr: `${air.destination?.airportNameFr} (${air.destination?.countryNameFr})`,
    destCode: air.destination.airportCode,
    cityName: air.destination.cityNameEn,
    adtNo: +air.adults,
    chdNo: air.children ?? 0,
    direct: air.directFlight,
    stops: 2,
    infNo: 1,
    depDate: formatDate(air.tripTypeSelected === ONEWAY ? air.depDate : air.dateRange[0], 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
    retDate: formatDate(air.tripTypeSelected === ONEWAY ? DateTime.now() : air.dateRange[1], 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
    flights: [],
    price: air.service,
    additionalNote: air.additionalNote,
    businessAdtNo: +air.businessAdults,
    businessChdNo: air.businessChildren ?? 0,
    isEconomy: air.isEconomy,
    isBusiness: air.isBusiness,
    returnFrom: air.returningFrom?.airportNameEn ?? '',
    returnFromFr: air.returningFrom?.airportNameFr ?? '',
    returnFromCode: air.returningFrom?.airportCode ?? '',
    returnTo: air.returningTo?.airportNameEn ?? '',
    returnToFr: air.returningTo?.airportNameFr ?? '',
    returnToCode: air.returningTo?.airportCode ?? ''
  };
}

const createFlightPayloadObject = (air: any) => {
  return {
    gateCode: air.gateway.airportCode,
    destCode: air.destination.airportCode,
    depDate: formatDate(air.tripTypeSelected === ONEWAY ? air.depDate : air.dateRange[0]),
    retDate: formatDate(air.tripTypeSelected === ONEWAY ? DateTime.now() : air.dateRange[1]),
    direct: air.directFlight
  };
};

const createPayloadObject = (airsTemp: any) => {
  const agentInfo = cloneDeep(isWorksheetRequote.value && !isClearAgencyInfo.value ? getUserQuote(quote.value) : getUserParam());
  return {
    ...agentInfo,
    lang: props.selectedLanguage,
    gName: '',
    gContact: '',
    gType: '',
    gTypeFr: '',
    gTypeId: '',
    qType: AIR_ONLY,
    advanced: false,
    airs: airsTemp,
    avgPaxRoom: props.avgPaxRoomData,
    originalQuote: renderOriginQuote(true, quote.value?.sk),
    isReplicate: isWorksheetRequote.value
  };
};

const renderNameAirline = (code: any) => {
  const airport = listReturning.value?.find((air) => air.airportCode === code);
  return `${airport?.airportNameEn} (${airport?.airportCode})`;
};

const airlineNameFr = (code: any) => {
  const airport = listReturning.value?.find((air) => air.airportCode === code);
  return `${airport?.airportNameFr} (${airport?.airportCode})`;
};

const renderDataAir = (flightData: any, type: any, p: any, listAirline: any) => {
  const newData = [];
  const cloneFlightData = { ...flightData };
  if (cloneFlightData.stopFlights) {
    delete cloneFlightData.stopFlights;
    newData.push(cloneFlightData, ...flightData.stopFlights);
  } else {
    newData.push(cloneFlightData);
  }
  const result = newData.map((item) => {
    return {
      type,
      depTime: item.depTime,
      arrTime: item.arrivalTime,
      flightNo: item.flight,
      gateCode: item.depAirport,
      chd: +p.chdNo,
      adt: +p.adtNo,
      gate: renderNameAirline(item.depAirport),
      gateFr: airlineNameFr(item.depAirport),
      destCode: item.arrivalAirport,
      dest: renderNameAirline(item.arrivalAirport),
      destFr: airlineNameFr(item.arrivalAirport),
      depDate: formatDate(item.depDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      arrDate: formatDate(item.arrivalDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      airline: listAirline?.find((air: any) => air.code === item.operated)?.name,
      airlineCode: item.operated,
      flightNumber: item.flightNumber,
      operated: item.operated,
      classesAvailable: item.classesAvailable,
      via: '',
      fClass: ''
    };
  });
  return result;
};

function updatePayloadAir(airs, listFlight, listAirline) {
  return airs?.map((p, idx) => {
    const newFlight = listFlight[idx].reduce((result, flight, flightIndex) => {
      if (p.itinerary === ONEWAY) {
        return [...result, ...renderDataAir(flight, 'Outbound', p, listAirline)];
      } else {
        return [...result, ...renderDataAir(flight, flightTypesOption[flightIndex]?.type, p, listAirline)];
      }
    }, []);

    p.flights = newFlight;
    return { ...p };
  });
}

const resetAirData = (air) => {
  air.gateway = '';
  air.destination = '';
  air.dateRange = [];
  air.depDate = null;
  air.adults = '';
  air.children = '';
  air.service = [{ type: 'Economy' }, { type: 'Business' }];
  air.directFlight = false;
  air.additionalNote = '';
  air.businessAdults = '';
  air.businessChildren = '';
  air.isEconomy = false;
  air.isBusiness = false;
  air.returningFrom = '';
  air.returningTo = '';
  air.tripTypeSelected = ROUNDTRIP;
};

const removeAirs = () => {
  airs.forEach((air) => {
    resetAirData(air);
  });
  if (airs.length > 1) {
    airs.splice(1);
  }
  viewQuoteStore.updateViewQuote({});
  viewQuoteStore.getAction('view');
  viewQuoteStore.getActionRequoteFromWorksheet(true);
};

const clearAllDataAirs = () => {
  removeAirs();
  emits('clear');
  isClearAgencyInfo.value = true;
};

const addAir = () => {
  airs.push({
    gateway: '',
    destination: '',
    dateRange: [],
    depDate: null,
    duration: '0',
    adults: '',
    children: '',
    service: [{ type: 'Economy' }, { type: 'Business' }],
    directFlight: false,
    businessAdults: '',
    businessChildren: '',
    isEconomy: false,
    isBusiness: false,
    returningFrom: '',
    returningTo: '',
    tripTypeSelected: ROUNDTRIP
  });
  isReturnDate.value.push(false);
};

const removeAir = () => {
  airs.pop();
  isReturnDate.value.pop();
};

const searchGateway = (event: any) => {
  const gateListSorted = gatewayAirports.value.sort((firstValue: any, secondValue: any) => firstValue[`airportName${capitalize(currentLanguage)}`]?.localeCompare(secondValue[`airportName${capitalize(currentLanguage)}`]));
  if (!event.query?.trim().length) {
    filterGate.value = [...gateListSorted.slice(0, 100)];
  } else {
    filterGate.value = gateListSorted.filter((air: any) => {
      return (
        air.airportNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionEn?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const searchDestination = (event: any) => {
  const destListSorted = destinationAirports.value.sort((firstValue: any, secondValue: any) => firstValue[`airportName${capitalize(currentLanguage)}`]?.localeCompare(secondValue[`airportName${capitalize(currentLanguage)}`]));
  if (!event.query?.trim().length) {
    filterDes.value = [...destListSorted.slice(0, 100)];
  } else {
    filterDes.value = destListSorted.filter((air: any) => {
      return (
        air.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameFr?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const searchReturningFrom = (event: any) => {
  const returnFromListSorted = airportsReturningFrom.value.sort((firstValue: any, secondValue: any) => firstValue[`airportName${capitalize(currentLanguage)}`]?.localeCompare(secondValue[`airportName${capitalize(currentLanguage)}`]));
  if (!event.query?.trim().length) {
    filterReturningFrom.value = [...returnFromListSorted.slice(0, 100)];
  } else {
    filterReturningFrom.value = returnFromListSorted.filter((air: any) => {
      return (
        air.airportNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameFr?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const searchReturningTo = (event: any) => {
  const returnToListSorted = airportsReturningTo.value.sort((firstValue: any, secondValue: any) => firstValue[`airportName${capitalize(currentLanguage)}`]?.localeCompare(secondValue[`airportName${capitalize(currentLanguage)}`]));
  if (!event.query?.trim().length) {
    filterReturningTo.value = [...returnToListSorted.slice(0, 100)];
  } else {
    filterReturningTo.value = returnToListSorted.filter((air: any) => {
      return (
        air.airportNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.cityNameEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.regionEn?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameFr?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryNameEn?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const clearReturningFrom = (index: number) => {
  airs[index].returningFrom = '';
};

const clearReturningTo = (index: number) => {
  airs[index].returningTo = '';
};

const clearGateway = (index: number) => {
  airs[index].gateway = '';
};

const clearDestination = (index: number) => {
  airs[index].destination = '';
};

const changeCalendar = (index: number) => {
  if (airs[index].dateRange?.[1]) {
    isReturnDate.value[index] = false;
  } else {
    isReturnDate.value[index] = true;
  }

  diffDays.value = DateTime.fromJSDate(new Date(airs[index].dateRange[1]))
    .diff(DateTime.fromJSDate(new Date(airs[index].dateRange[0])), 'days')
    .toObject().days;
  diffDays.value = Math.ceil(diffDays.value);
  if (diffDays.value < 0) {
    airs[index].duration = '0';
    return;
  }
  if (isNaN(diffDays.value) || diffDays.value === 0) {
    airs[index].duration = '1';
  } else {
    airs[index].duration = diffDays.value.toString();
  }
};

getListAirline();

const updateIsEconomy = (value: boolean, index: number) => {
  if (!value) {
    airs[index].adults = 0;
    airs[index].children = 0;
  }
};
const updateIsBusiness = (value: boolean, index: number) => {
  if (!value) {
    airs[index].businessAdults = 0;
    airs[index].businessChildren = 0;
  }
};

const renderQuoteTabIntroHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-term', innerHTML: props.quoteIntro.termsFr });
  } else {
    return h('div', { class: 'english-term', innerHTML: props.quoteIntro.terms });
  }
};

const changeTypeTrip = (index: number) => {
  airs[index].gateway = '';
  airs[index].destination = '';
  airs[index].dateRange = [];
  airs[index].duration = '0';
  airs[index].depDate = null;
  airs[index].returningFrom = '';
  airs[index].returningTo = '';
};

const handleDateLimitationAir = (advancedFormAir: any) => {
  if (advancedFormAir) {
    minDateRangeValue.value = isWorksheetRequote.value ? new Date(DateTime.utc().plus({ days: 1 })) : getLimitDate(advancedFormAir, limitCalendar.value, 'startDateConfig');
    maxDateRangeValue.value = isWorksheetRequote.value ? undefined : getLimitDate(advancedFormAir, limitCalendar.value, 'endDateConfig');
  }
  disableDate.value = true;
};

const clearAirOptions = (maxVal: number) => {
  if (isRequote.value && isWorksheetRequote.value) {
    return;
  }

  if (maxVal && airs.length && maxVal < airs.length) {
    airs.splice(maxVal);
  }
};

watch(
  () => airs,
  (newValue) => {
    newValue.forEach((air, index) => {
      if (air.adults === null || air.adults === '') {
        airs[index].adults = 0;
      }
      if (air.children === null || air.children === '') {
        airs[index].children = 0;
      }
      if (air.businessAdults === null || air.businessAdults === '') {
        airs[index].businessAdults = 0;
      }
      if (air.businessChildren === null || air.businessChildren === '') {
        airs[index].businessChildren = 0;
      }
    });
  },
  { deep: true }
);

watch(
  () => airs,
  (newVal) => {
    newVal.forEach((air) => {
      if (air.tripTypeSelected === ROUNDTRIP) {
        isRoundtrip.value = true;
        isOpenJaw.value = false;
        isOneWay.value = false;
      }
      if (air.tripTypeSelected === OPENJAW) {
        isRoundtrip.value = false;
        isOpenJaw.value = true;
        isOneWay.value = false;
      }
      if (air.tripTypeSelected === ONEWAY) {
        isRoundtrip.value = false;
        isOpenJaw.value = false;
        isOneWay.value = true;
      }
    });
  },
  { deep: true, immediate: true }
);

watch(
  () => props.advancedForm,
  (newValue) => {
    if (!newValue) {
      return;
    }
    airs.forEach((air: any) => {
      air.depDate = undefined;
      air.dateRange = [];
    });
    handleDateLimitationAir(newValue[0].groupTypeId);
    if (newValue[0].groupType) {
      if (newValue[0].groupType.includes('Incentive')) {
        clearAirOptions(airOptionConfig.value?.incentiveMaxOptionPermitted);
        optionConfigValue.value = airOptionConfig.value?.incentiveMaxOptionPermitted;
      } else {
        clearAirOptions(airOptionConfig.value?.maxOptionPermitted);
        optionConfigValue.value = airOptionConfig.value?.maxOptionPermitted;
      }
      if (isRequote.value && isWorksheetRequote.value) {
        optionConfigValue.value = max([optionConfigValue.value, quote.value.airs?.length]);
      }
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => props.calendarData,
  (newValue) => {
    if (newValue) {
      limitCalendar.value = getQuoteDepartureData(newValue);
    }
  },
  { deep: true }
);

onMounted(() => {
  if (quote.value.airs) {
    if (isRequote.value) {
      quote.value.airs.forEach(async (air, index) => {
        if (index > 0) {
          airs.push({
            gateway: '',
            destination: '',
            depDate: null,
            duration: '0',
            dateRange: [],
            adults: '',
            children: '',
            service: [],
            directFlight: false,
            additionalNote: '',
            businessAdults: '',
            businessChildren: '',
            isEconomy: false,
            isBusiness: false,
            returningFrom: '',
            returningTo: '',
            tripTypeSelected: ROUNDTRIP
          });
        }
        airs[index].gateway = await cityStore.getAirportCityDetail(air.gateCode);
        airs[index].destination = await cityStore.getAirportCityDetail(air.destCode);
        airs[index].adults = air.adtNo;
        airs[index].children = air.chdNo;
        airs[index].directFlight = air.direct;
        airs[index].additionalNote = air.additionalNote;
        airs[index].businessAdults = air.businessAdtNo ?? 0;
        airs[index].businessChildren = air.businessChdNo ?? 0;
        airs[index].isEconomy = air.isEconomy ?? false;
        airs[index].isBusiness = air.isBusiness ?? false;
        airs[index].returningFrom = await cityStore.getAirportCityDetail(air.returnFromCode);
        airs[index].returningTo = await cityStore.getAirportCityDetail(air.returnToCode);
        airs[index].tripTypeSelected = air.itinerary;
        airs[index].duration = '0';
        airs[index].service = [{ type: 'Economy' }, { type: 'Business' }];
      });
    }
  }
});
</script>
<template>
  <h3 v-if="isFlight" class="m-2">{{ $t('common.flight') }}</h3>
  <LoadingSpinner v-if="isLoading || submitQuoteStore.isLoading" />
  <div class="mb-3" v-if="quoteIntro">
    <renderQuoteTabIntroHtml></renderQuoteTabIntroHtml>
  </div>
  <div v-for="(option, index) in airs" :key="index">
    <Fluid>
      <p v-if="advancedForm">{{ $t('common.option') }} {{ index + 1 }}</p>
      <p @click="handleRedirectFlight()" class="cursor-pointer flex gap-2 align-items-center justify-content-end"><i class="text-3xl pi pi-exclamation-circle exclamation-icon"></i> {{ $t('common.where_we_fly') }}</p>
      <div class="flex flex-wrap gap-4 mb-4">
        <TripType v-model:referenceType="option.tripTypeSelected" :index="index" @changeTypeTrip="changeTypeTrip(index)" />
      </div>
      <AirGatewayDestinationSelect
        :optionAir="option"
        :filterGate="filterGate"
        :filterDes="filterDes"
        :isShowError="isShowError"
        @searchDestination="($event: any) => searchDestination($event)"
        @searchGateway="($event: any) => searchGateway($event)"
        @clearGateway="clearGateway(index)"
        @clearDestination="clearDestination(index)"
      ></AirGatewayDestinationSelect>
    </Fluid>
    <div v-if="option.tripTypeSelected === OPENJAW">
      <AirOpenJawReturningForm
        :optionAir="option"
        :filterReturningFrom="filterReturningFrom"
        :filterReturningTo="filterReturningTo"
        :isShowError="isShowError"
        @searchReturningFrom="($event: any) => searchReturningFrom($event)"
        @searchReturningTo="($event: any) => searchReturningTo($event)"
        @clearReturningFrom="clearReturningFrom(index)"
        @clearReturningTo="clearReturningTo(index)"
      ></AirOpenJawReturningForm>
    </div>

    <div>
      <AirCalendarDurationForm
        :optionAir="option"
        :maxDateRangeValue="maxDateRangeValue"
        :minDateRangeValue="minDateRangeValue"
        :isShowError="isShowError"
        :disableDate="disableDate"
        :isReturnDate="isReturnDate[index]"
        @changeDateRange="changeCalendar(index)"
      ></AirCalendarDurationForm>
    </div>

    <div class="grid">
      <div class="col-12 md:col-6 economy-class">
        <AirClassInputNumber
          v-model:optionAir="option.isEconomy"
          v-model:optionAdults="option.adults"
          v-model:optionChild="option.children"
          :optionIndex="index"
          :isShowError="isShowError"
          :classType="true"
          :errorMessage="v$.$each.$response.$errors[index].adults[0]?.$message"
          @updateClass="($event: any, optionIndex: number) => updateIsEconomy($event, optionIndex)"
        ></AirClassInputNumber>
      </div>
      <div class="col-12 md:col-6 business-class">
        <AirClassInputNumber
          v-model:optionAir="option.isBusiness"
          v-model:optionAdults="option.businessAdults"
          v-model:optionChild="option.businessChildren"
          :optionIndex="index"
          :isShowError="isShowError"
          :classType="false"
          :errorMessage="v$.$each.$response.$errors[index].businessAdults[0]?.$message"
          @updateClass="($event: any, optionIndex: number) => updateIsBusiness($event, optionIndex)"
        ></AirClassInputNumber>
      </div>
    </div>

    <div>
      <AirAdditionalForm :optionAir="option" :isAvancedForm="isAvancedForm" :isFlight="isFlight" :isShowError="isShowError" :errorMessage="v$.$each.$response.$errors[index].service[0]?.$message"></AirAdditionalForm>
    </div>
  </div>
  <div class="grid" v-if="advancedForm">
    <div id="add-remove-air" class="flex w-full justify-content-around">
      <a href="javascript:" class="block text-primary text-left mb-3 remove-air" @click="removeAir()" v-if="airs.length > 1">- {{ $t('advance_quote.form.remove_air') }} </a>
      <a href="javascript:" class="block text-primary text-right mb-3 add-air" v-if="airs.length < optionConfigValue" @click="addAir()">+ {{ $t('advance_quote.form.add_air') }}</a>
    </div>
  </div>
  <div class="flex gap-3">
    <Button id="submit-btn" :label="$t('advance_quote.form.submit')" class="mt-2 bg-primary border-noround submit-btn" v-if="!isFlight" @click="handleCheckBrochure()"></Button>
    <Button id="clear-btn" :label="$t('common.buttons.clear')" class="mt-2 bg-primary border-noround clear-btn" v-if="!isFlight" @click="clearAllDataAirs()"></Button>
  </div>
  <p class="mt-3" v-if="!isAvancedForm">
    {{ $t('advance_quote.form.for_more') }} <a href="#/external/search/air" class="text-primary">{{ $t('advance_quote.form.click_here') }}</a
    >.
  </p>
</template>
